import type { CSSProperties } from "react";
import {
  DomainCard,
  Eyebrow,
  cn,
  domainClasses,
  type Domain,
} from "@appcaire/ui";
import { howItWorksCopy } from "./howItWorksCopy";
import type { LandingLocale } from "../landingCopy";
import {
  homeSectionClass,
  homeSectionHeadClass,
  homeSectionTitleClass,
} from "./HomeParts";

/** Minutes from 07:00 on a 07:00–10:00 axis. */
const AXIS = 180;
const at = (start: number, width = 30): CSSProperties => ({
  left: `${(start / AXIS) * 100}%`,
  width: `${(width / AXIS) * 100}%`,
});
const FLEX = at(45, 30 + 30);

type Bar = { start: number; className: string; flex?: string };

const TRADITIONAL: Bar[] = [
  { start: 60, className: "bg-caire-muted/35" },
  { start: 80, className: "bg-caire-muted" },
  { start: 70, className: "border-[1.5px] border-dashed border-caire-muted" },
  { start: 105, className: "bg-domain-human-solid" },
];
const CAIRE_ROWS: Domain[] = ["human", "planning", "continuity", "care"];
const CAIRE: Bar[] = CAIRE_ROWS.map((domain, i) => ({
  start: i === 0 || i === 2 ? 60 : 69,
  className: domainClasses[domain].solid,
  flex: domainClasses[domain].tint,
}));

function Timeline({
  title,
  tag,
  tagDomain,
  text,
  rows,
  values,
  bars,
  lateNote,
  surface,
}: {
  title: string;
  tag: string;
  tagDomain: Domain;
  text: string;
  rows: string[];
  values: string[];
  bars: Bar[];
  lateNote?: string;
  surface: "tint" | "white";
}) {
  return (
    <DomainCard domain="society" surface={surface} className="gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[20px] font-extrabold text-caire-ink">{title}</h3>
        <span
          className={cn(
            "rounded-full px-3 py-1 font-mono text-xs font-semibold",
            domainClasses[tagDomain].tint,
            domainClasses[tagDomain].ink,
          )}
        >
          {tag}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="grid grid-cols-[7.5rem_minmax(0,1fr)] font-mono text-[11px] text-caire-muted"
      >
        <span />
        <div className="flex justify-between">
          <span>07:00</span>
          <span>08:00</span>
          <span>09:00</span>
          <span>10:00</span>
        </div>
      </div>
      <dl className="flex flex-col gap-2.5">
        {rows.map((row, i) => (
          <div
            key={row}
            className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-0"
          >
            <dt className="text-[13px] font-semibold text-caire-ink">{row}</dt>
            <dd className="relative h-8">
              {bars[i].flex ? (
                <span
                  aria-hidden="true"
                  className={cn("absolute inset-y-0 rounded-md", bars[i].flex)}
                  style={FLEX}
                />
              ) : null}
              <span
                aria-hidden="true"
                className={cn(
                  "absolute inset-y-1 rounded-md",
                  bars[i].className,
                )}
                style={at(bars[i].start)}
              />
              <span
                className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-2 font-mono text-[11px] font-semibold text-caire-ink"
                style={{ left: `${((bars[i].start + 30) / AXIS) * 100}%` }}
              >
                {values[i]}
              </span>
            </dd>
          </div>
        ))}
      </dl>
      {lateNote ? (
        <p className="-mt-1 text-right font-mono text-[11px] font-semibold text-domain-human-ink">
          {`${rows[rows.length - 1]}: ${lateNote}`}
        </p>
      ) : null}
      <p className="text-[15px] leading-normal text-caire-ink/85">{text}</p>
    </DomainCard>
  );
}

/** Traditionellt vs Caire: the same breakfast on a 07–10 timeline (3b). */
export function TraditionalVsCaireSection({
  locale,
}: {
  locale: LandingLocale;
}) {
  const c = howItWorksCopy[locale].compare;
  return (
    <section
      className={homeSectionClass}
      aria-labelledby="compare-heading"
      data-how-section="compare"
    >
      <div className={homeSectionHeadClass}>
        <div>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 id="compare-heading" className={homeSectionTitleClass}>
            {c.title}
          </h2>
        </div>
        <p>{c.lead}</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Timeline
          {...c.traditional}
          tagDomain="human"
          values={c.rowValues.traditional}
          bars={TRADITIONAL}
          lateNote={c.lateNote}
          surface="tint"
        />
        <Timeline
          {...c.caire}
          tagDomain="care"
          values={c.rowValues.caire}
          bars={CAIRE}
          surface="white"
        />
      </div>
    </section>
  );
}
